import { Editor } from '@tiptap/react';
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Underline as UnderlineIcon } from 'lucide-react';
import React from 'react';

import { Button } from 'DesignSystem/ui/button';
import { Input } from 'DesignSystem/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'DesignSystem/ui/popover';
import { cn } from 'DesignSystem/utils';

type ToolbarButtonProps = {
  label: string;
  isActive?: boolean;
  onClick: () => void;
  children: React.ReactNode;
};

function ToolbarButton({ label, isActive, onClick, children }: ToolbarButtonProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-mini"
      aria-label={label}
      aria-pressed={isActive}
      onClick={onClick}
      className={cn(
        'h-7 w-7 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900',
        isActive && 'bg-zinc-200 text-zinc-900'
      )}
    >
      {children}
    </Button>
  );
}

function normalizeHref(raw: string): string | null {
  const href = raw.trim();
  if (!href) return null;
  if (/^(javascript|data|vbscript):/i.test(href)) return null;
  if (/^(https?:\/\/|\/|#)/i.test(href)) return href;
  return `https://${href}`;
}

function LinkControl({ editor }: { editor: Editor }) {
  const [open, setOpen] = React.useState(false);
  const [href, setHref] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setHref(editor.getAttributes('link').href ?? '');
  }, [editor, open]);

  function applyLink() {
    const next = normalizeHref(href);
    if (!next) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: next }).run();
    }
    setOpen(false);
  }

  function removeLink() {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-mini"
          aria-label="Link"
          aria-pressed={editor.isActive('link')}
          className={cn(
            'h-7 w-7 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900',
            editor.isActive('link') && 'bg-zinc-200 text-zinc-900'
          )}
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-2 p-3" align="start">
        <Input
          aria-label="Link URL"
          placeholder="https://"
          value={href}
          onChange={(event) => setHref(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              applyLink();
            }
          }}
        />
        <div className="flex justify-end gap-2">
          {editor.isActive('link') ? (
            <Button type="button" variant="outline" size="sm" onClick={removeLink}>
              Remove
            </Button>
          ) : null}
          <Button type="button" size="sm" onClick={applyLink}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function RichEditorToolbar({ editor }: { editor: Editor }) {
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-zinc-200 bg-zinc-50 px-2 py-1">
      <ToolbarButton
        label="Bold"
        isActive={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        isActive={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Underline"
        isActive={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Bulleted list"
        isActive={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        isActive={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>
      <LinkControl editor={editor} />
    </div>
  );
}
